import type Highcharts from 'highcharts';
import type { Axis } from 'highcharts/highcharts.src';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { AGE_LABEL, chartColors, LIFE_EVENT_PANE, LIFE_EVENT_TITLE, X_AXIS_OFFSET, YEAR_LABEL } from '../../config/constants';

interface IProps {
    clientBirthdate?: string;
    isClientOutput?: boolean;
    isProjection?: boolean;
}

export const useChartBaseCustomizationCallbacks = ({ clientBirthdate, isClientOutput, isProjection }: IProps) => {
    const { t } = useTranslation();

    const addLifeEventPane = useCallback(
        (chart: Highcharts.Chart) => {
            if (isClientOutput) {
                return;
            }
            const xAxis = chart.xAxis[0] as Axis;
            // @ts-expect-error Problems with missing Highcharts types
            const xAxisBox = xAxis['gridGroup']?.getBBox();
            // @ts-expect-error Problems with missing Highcharts types
            const lifeEventPane = chart[LIFE_EVENT_PANE];
            // @ts-expect-error Problems with missing Highcharts types
            const lifeEventTitle = chart[LIFE_EVENT_TITLE];
            if (lifeEventPane) lifeEventPane.destroy();
            if (lifeEventTitle) lifeEventTitle.destroy();

            // @ts-expect-error Problems with missing Highcharts types
            chart[LIFE_EVENT_PANE] = chart.renderer
                .rect(0, chart.plotHeight + (xAxisBox?.y ?? 0) + 1, chart.chartWidth - 10, (isClientOutput ? 2 * X_AXIS_OFFSET : X_AXIS_OFFSET) - 1)
                .attr({
                    'stroke-width': 0,
                    fill: isClientOutput ? chartColors.EVENT_PANE_BACKGROUND_CLIENT_OUTPUT : chartColors.EVENT_PANE_BACKGROUND,
                    zIndex: 0,
                    class: 'life-event-background',
                })
                .add();
            // @ts-expect-error Problems with missing Highcharts types
            chart[LIFE_EVENT_TITLE] = chart.renderer
                .text(t('lifeplanEventsTitle'), 10, chart.plotHeight + (xAxisBox?.y ?? 0) + (X_AXIS_OFFSET + 9) / 2)
                .attr({ zIndex: 1, class: isClientOutput ? 'client-output-label-life-event' : 'label-life-event' })
                .add();
        },
        [isClientOutput, t],
    );

    const addCustomLabels = useCallback(
        (chart: Highcharts.Chart) => {
            const labelOffset = isClientOutput ? 0 : 3;
            // @ts-expect-error Problems with missing Highcharts types
            const ageLabel = chart[AGE_LABEL];
            // @ts-expect-error Problems with missing Highcharts types
            const yearLabel = chart[YEAR_LABEL];
            if (yearLabel) {
                yearLabel.destroy();
            }
            if (ageLabel) {
                ageLabel.destroy();
            }
            // @ts-expect-error Problems with missing Highcharts types
            const bbox = chart.xAxis[0]['labelGroup']?.getBBox();
            if (!bbox || bbox.y < (chart.plotTop ?? 0)) {
                return;
            }
            const clientOutputClassPrefix = isProjection ? 'client-projection-output-' : 'client-output-';
            if (clientBirthdate) {
                // @ts-expect-error Problems with missing Highcharts types
                chart[AGE_LABEL] = chart.renderer
                    .label(t('clientAge'), 10, bbox.y)
                    .addClass(isClientOutput ? `${clientOutputClassPrefix}label-age` : 'label-age')
                    .add();
            }
            // @ts-expect-error Problems with missing Highcharts types
            chart[YEAR_LABEL] = chart.renderer
                .label(t('year'), 10, bbox.y + bbox.height / 2 + labelOffset)
                .addClass(isClientOutput ? `${clientOutputClassPrefix}label-year` : 'label-year')
                .add();
        },
        [clientBirthdate, isClientOutput, isProjection, t],
    );

    const formatXaxis = useCallback((axisLine: any) => {
        const axisLineD = axisLine?.element?.getAttribute('d')?.split(' ');
        if (axisLine != null) {
            axisLineD[1] = 0;
            axisLine.element.setAttribute('d', axisLineD.join(' '));
        }
    }, []);

    const formatZeroPlotline = useCallback(
        (chart: Highcharts.Chart) => {
            if (isClientOutput) {
                // @ts-expect-error Problems with missing Highcharts types
                const existingZeroLine = chart['zeroPlotLineRect'];
                if (existingZeroLine) {
                    existingZeroLine.destroy();
                    // @ts-expect-error Problems with missing Highcharts types
                    chart['zeroPlotLineRect'] = undefined;
                }
                // @ts-expect-error Problems with missing Highcharts types
                const plotLineBox = chart.axes[1]['plotLinesAndBandsGroups']['lines-999']?.getBBox();
                if (plotLineBox && plotLineBox.y >= (chart.plotTop ?? 0)) {
                    // @ts-expect-error Problems with missing Highcharts types
                    chart['zeroPlotLineRect'] = chart.renderer
                        .rect(0, plotLineBox.y, chart.chartWidth - 10, 4, 0)
                        .attr({
                            fill: '#D0CCB2',
                            zIndex: 3,
                        })
                        .add();
                }
            } else {
                const zeroPlotlLine = document.getElementsByClassName('highcharts-plot-lines-999');
                if (zeroPlotlLine.length > 0) {
                    const dZeroPlotLine = zeroPlotlLine[0]?.children[0]?.getAttribute('d')?.split(' ');
                    if (dZeroPlotLine && dZeroPlotLine.length > 2) {
                        dZeroPlotLine[1] = '0';
                        zeroPlotlLine[0].children[0].setAttribute('d', dZeroPlotLine.join(' '));
                    }
                }
            }
        },
        [isClientOutput],
    );

    const getYCoordinate = useCallback((line: Element) => Number.parseInt(line.getAttribute('d')?.split(' ')[2] ?? '', 10), []);

    const formatZeroPlotlineForClientOutput = useCallback(
        (plotLines: Element[]) => {
            if (!plotLines || !Array.isArray(plotLines) || plotLines.length === 0) return;

            let lowestLine = plotLines[0];
            Object.values(plotLines).forEach((line: any) => {
                lowestLine = getYCoordinate(lowestLine) < getYCoordinate(line) ? line : lowestLine;
            });
            lowestLine?.setAttribute('visibility', 'true');
            lowestLine?.setAttribute('stroke', '#191919');
            lowestLine?.setAttribute('stroke-width', '2.5');
        },
        [getYCoordinate],
    );

    const formatPlotlines = useCallback(
        (plotLines: any) => {
            if (!plotLines) return;

            if (isClientOutput) {
                formatZeroPlotlineForClientOutput(plotLines);
            }
            Object.values(plotLines).forEach((line: any) => {
                const d = line.getAttribute('d')?.split(' ');
                if (d?.length >= 2) {
                    d[1] = '0';
                    line.setAttribute('d', d.join(' '));
                } else {
                    return;
                }
            });
        },
        [formatZeroPlotlineForClientOutput, isClientOutput],
    );

    const changePlotLinesStyle = useCallback(
        (chart: Highcharts.Chart) => {
            const xAxisOptions = chart.xAxis[0] as Axis;
            const yAxisOptions = chart.yAxis[0] as Axis;
            // @ts-expect-error Problems with missing Highcharts types
            const axisLine = xAxisOptions['axisLine'];
            // @ts-expect-error Problems with missing Highcharts types
            const plotLines = yAxisOptions['gridGroup']?.element?.children;

            formatPlotlines(plotLines);
            formatZeroPlotline(chart);
            formatXaxis(axisLine);
        },
        [formatPlotlines, formatXaxis, formatZeroPlotline],
    );

    const putTicksToFront = useCallback((chart: Highcharts.Chart) => {
        const ticks = chart.xAxis[0].ticks;
        if (ticks) {
            Object.values(ticks).forEach((tick) => tick.mark?.toFront());
        }
    }, []);

    return useCallback(
        (chart: Highcharts.Chart) => {
            changePlotLinesStyle(chart);
            addLifeEventPane(chart);
            addCustomLabels(chart);
            putTicksToFront(chart);
        },
        [addLifeEventPane, addCustomLabels, changePlotLinesStyle, putTicksToFront],
    );
};
